<script setup lang="ts">
  import { computed } from "vue";
  import { RefreshCw, Trash2 } from "lucide-vue-next";
  import { storeToRefs } from "pinia";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { IndexTable } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { Button } from "@/components/ui/button";
  import { Skeleton } from "@/components/ui/skeleton";
  import { useGridsStore } from "@stores/survey/grids/grids";
  import GridAnswerCell from "./GridAnswerCell.vue";
  import GridColumnMenu from "./GridColumnMenu.vue";
  import GridSummarySegments from "./GridSummarySegments.vue";
  import { columnKey, SEPARATED } from "./gridColumns";
  import type { GridColumn, GridRow } from "@/types";

  const SUMMARY_KEY = "summary";

  type Item = GridRow | { id: typeof SUMMARY_KEY };

  const emit = defineEmits<{
    "select-cell": [row: GridRow, column: GridColumn];
    "select-summary": [column: GridColumn];
    "reorder-columns": [columns: GridColumn[]];
    "reorder-rows": [rows: GridRow[]];
    "remove-column": [column: GridColumn];
    "remove-row": [row: GridRow];
    "refresh-row": [row: GridRow];
    "refresh-summary": [column: GridColumn];
  }>();

  const gridsStore = useGridsStore();
  const { currentGrid, populations } = storeToRefs(gridsStore);

  const columns = computed(() => (currentGrid.value?.columns ?? []).filter((column) => column.kind === "ai_question"));
  const rows = computed(() => currentGrid.value?.rows ?? []);
  const summaryVisible = computed(() => rows.value.length > 1 && columns.value.length > 0);
  const items = computed<Item[]>(() => {
    const list: Item[] = [...rows.value];
    if (summaryVisible.value) list.unshift({ id: SUMMARY_KEY });
    return list;
  });

  const isSummary = (item: Item): item is { id: typeof SUMMARY_KEY } => item.id === SUMMARY_KEY;

  const tableColumns = computed<DataTableColumn<Item>[]>(() => [
    { key: "population", label: "Population", width: 260 },
    ...columns.value.map((column) => ({ key: columnKey(column), label: column.prompt ?? "", width: 260, reorderable: true, ...SEPARATED })),
  ]);

  const onReorderColumns = (keys: string[]) =>
    emit(
      "reorder-columns",
      keys.flatMap((key) => columns.value.filter((column) => columnKey(column) === key)),
    );
  const onReorderRows = (ordered: Item[]) => emit("reorder-rows", ordered.filter((item): item is GridRow => !isSummary(item)));

  const populationFor = (row: GridRow) => populations.value.find((population) => population.id === row.search_group_id);
  const cellFor = (column: GridColumn, row: GridRow) => column.cells.find((cell) => cell.search_group_id === row.search_group_id);
  const summaryFor = (column: GridColumn) =>
    (currentGrid.value?.summaries ?? []).find((summary) => summary.grid_column_id === column.id);
  const summaryRefreshable = (column: GridColumn) => {
    const summary = summaryFor(column);
    return !!summary && (summary.outdated || summary.status === "failed");
  };
  const refreshableSummaries = computed(() => columns.value.filter(summaryRefreshable));
  const rowRefreshable = (row: GridRow) => row.outdated || columns.value.some((column) => cellFor(column, row)?.status === "failed");
</script>

<template>
  <IndexTable
    :items="items"
    :columns="tableColumns"
    :total="items.length"
    :page-size="items.length || 1"
    actions-class="w-[56px]"
    resizable
    reorderable-columns
    :reorderable-rows="(item: Item) => !isSummary(item)"
    empty-message="No populations in this grid."
    @reorder-columns="onReorderColumns"
    @reorder-rows="onReorderRows">
    <template
      v-for="column in columns"
      :key="`header-${columnKey(column)}`"
      #[`header-${columnKey(column)}-actions`]>
      <AiStar class="size-3.5 shrink-0 text-primary" />
      <GridColumnMenu @remove="emit('remove-column', column)" />
    </template>

    <template #cell-population="{ item }">
      <template v-if="isSummary(item)">
        <span class="flex items-center gap-2 font-medium">
          <AiStar class="size-3.5 shrink-0 text-primary" />
          Summary
        </span>
        <span class="mt-0.5 block text-xs text-muted-foreground">Across {{ rows.length }} populations</span>
        <button
          v-if="refreshableSummaries.length > 1"
          type="button"
          class="mt-1 block cursor-pointer text-xs text-primary hover:underline"
          @click.stop="refreshableSummaries.forEach((column) => emit('refresh-summary', column))">
          Refresh all summaries
        </button>
      </template>
      <template v-else>
        <span class="flex items-center gap-2 font-medium">
          <span
            class="size-2.5 shrink-0 rounded-full"
            :style="{ backgroundColor: populationFor(item)?.color ?? undefined }" />
          <span class="min-w-0 truncate">{{ populationFor(item)?.name ?? "Population" }}</span>
        </span>
        <span
          v-if="item.respondents_count !== undefined"
          class="mt-0.5 block text-xs text-muted-foreground">
          {{ item.respondents_count }} respondents
        </span>
        <button
          v-if="rowRefreshable(item)"
          type="button"
          class="mt-1 flex cursor-pointer items-center gap-1 text-xs font-medium text-status-warning-strong hover:underline"
          @click.stop="emit('refresh-row', item)">
          {{ item.outdated ? "Population changed" : "Generation failed" }}
          <RefreshCw class="size-3" />
        </button>
      </template>
    </template>

    <template
      v-for="column in columns"
      :key="columnKey(column)"
      #[`cell-${columnKey(column)}`]="{ item }">
      <template v-if="isSummary(item)">
        <button
          v-if="summaryFor(column)?.segments?.length"
          type="button"
          class="block w-full cursor-pointer text-left"
          @click.stop="emit('select-summary', column)">
          <span class="line-clamp-2 whitespace-normal leading-snug">
            <GridSummarySegments :segments="summaryFor(column)?.segments ?? []" />
          </span>
        </button>
        <button
          v-if="summaryRefreshable(column)"
          type="button"
          class="mt-1 flex cursor-pointer items-center gap-1 text-xs font-medium text-status-warning-strong hover:underline"
          @click.stop="emit('refresh-summary', column)">
          {{ summaryFor(column)?.status === "failed" ? "Summary failed" : "Populations changed" }}
          <RefreshCw class="size-3" />
        </button>
        <Skeleton
          v-else-if="!summaryFor(column)?.segments?.length"
          class="h-3 w-3/4" />
      </template>
      <GridAnswerCell
        v-else
        :cell="cellFor(column, item)"
        @select="emit('select-cell', item, column)" />
    </template>

    <template #actions="{ item }">
      <Button
        v-if="!isSummary(item)"
        variant="ghost"
        size="icon-sm"
        class="text-foreground-subtle hover:text-destructive"
        aria-label="Remove from grid"
        @click.stop="emit('remove-row', item)">
        <Trash2 class="size-4" />
      </Button>
    </template>
  </IndexTable>
</template>
